/**
 * Canonical audit action names — Oshon's controlled vocabulary.
 *
 * Every primitive and data component draws from this catalog when
 * emitting audit events. Ad-hoc strings are a lint smell: consumers who
 * index on `action === 'click'` (without a component namespace) end up
 * with ambiguous event streams, and downstream governance (§5 ARCH
 * `@oshon-ai/governance`) can't classify what it's seeing.
 *
 * Naming convention (ADR-004):
 *   `<namespace>:<verb>`
 *   - `<namespace>` is a primitive name (`button`, `dialog`, `tooltip`)
 *     or a resource class (`data`, `row`, `settings`). Always lowercase.
 *   - `<verb>` is present-tense (`click`, `open`, `sort`) or a past
 *     participle when the event fires after-the-fact (`denied`).
 *
 * See ARCHITECTURE.md §10 for the built-in actions list and the rule
 * that governance reports group by namespace.
 */

/** Primitive-emitted actions (Phase 3a + 3b scope). */
export const PRIMITIVE_ACTIONS = {
  // Button
  BUTTON_CLICK: 'button:click',

  // Dialog
  DIALOG_OPEN: 'dialog:open',
  DIALOG_CLOSE: 'dialog:close',

  // Tooltip — deliberately emits none (ADR-003). Reserved for future use.

  // Phase 3b primitives. Exported now so Phase 4 data components can
  // reference them without reshuffling this file on every primitive land.
  INPUT_CHANGE: 'input:change',
  INPUT_BLUR: 'input:blur',
  SELECT_OPEN: 'select:open',
  SELECT_CLOSE: 'select:close',
  SELECT_CHANGE: 'select:change',
  POPOVER_OPEN: 'popover:open',
  POPOVER_CLOSE: 'popover:close',
  TABS_CHANGE: 'tabs:change',
  TOGGLE_CHANGE: 'toggle:change',
} as const;

/** Data-component actions (Phase 4+ scope per ARCHITECTURE §10). */
export const DATA_ACTIONS = {
  DATA_READ: 'data:read',
  DATA_SORT: 'data:sort',
  DATA_FILTER: 'data:filter',
  DATA_SELECT: 'data:select',
  DATA_BULK: 'data:bulk',
  DATA_EXPORT: 'data:export',
  DATA_EDIT: 'data:edit',
  /** Multi-level row grouping (Phase B). `details.grouping = string[]`. */
  DATA_GROUP: 'data:group',
  /** Row expansion — group collapse/expand or master-detail (Phase B/C). */
  DATA_EXPAND: 'data:expand',
  /** Column-order transition (Phase D drag-to-reorder). `details.order = string[]`. */
  DATA_REORDER: 'data:reorder',
  /** Column-visibility transition (Phase D show/hide). `details.visibility = Record<string, boolean>`. */
  DATA_VISIBILITY: 'data:visibility',
  /** Cell-range transition (Phase F spreadsheet-style selection). `details.range = { r1, c1, r2, c2 }`. */
  DATA_RANGE: 'data:range',
  /** Fill-handle commit (Phase F). `details.writes = Record<"r:c", value>`. */
  DATA_FILL: 'data:fill',
  /** Column pin / unpin (freeze). `details.pinning = { left: string[], right: string[] }`. */
  DATA_PIN: 'data:pin',

  ROW_VIEW: 'row:view',
  ROW_EDIT: 'row:edit',
  ROW_DELETE: 'row:delete',

  SETTINGS_VIEW: 'settings:view',
  SETTINGS_EDIT: 'settings:edit',

  PERMISSION_DENIED: 'permission:denied',
} as const;

/**
 * The full canonical catalog. Governance / drift-detection (Phase 5)
 * walks this set to classify event streams.
 */
export const CANONICAL_ACTIONS = {
  ...PRIMITIVE_ACTIONS,
  ...DATA_ACTIONS,
} as const;

/** Type-level union of every canonical action. */
export type CanonicalAction =
  (typeof CANONICAL_ACTIONS)[keyof typeof CANONICAL_ACTIONS];

/** True if `s` is a recognised canonical action. */
export function isCanonicalAction(s: string): s is CanonicalAction {
  return (Object.values(CANONICAL_ACTIONS) as string[]).includes(s);
}

/**
 * Parse a namespaced action into `{ namespace, verb }`. Returns `null`
 * for malformed strings (missing colon, empty halves). Governance reports
 * use this to group event streams by namespace.
 */
export function parseAction(
  action: string,
): { namespace: string; verb: string } | null {
  const idx = action.indexOf(':');
  if (idx <= 0 || idx === action.length - 1) return null;
  return {
    namespace: action.slice(0, idx),
    verb: action.slice(idx + 1),
  };
}
